import { useEffect } from "react";

/**
 * Qui édite STRATERA — la page qui met un éditeur derrière la marque.
 *
 * Le produit se vend sur sa vérifiabilité : méthode publiée, dérivation
 * citable, attestations contrôlables. Il manquait le premier maillon de cette
 * chaîne — *qui* affirme tout cela. La distinction STRATERA / RISEAFRICA
 * existait dans les mentions légales, c'est-à-dire là où personne ne la lit ;
 * cette page la dit en clair, avec ce que le produit refuse de faire, parce
 * que les refus sont la partie la plus crédible d'une promesse.
 *
 * Les identités juridiques précises (forme, RCCM, NCC, siège) restent dans
 * les mentions légales, leur place naturelle — et le vérificateur de
 * déploiement continue d'exiger qu'elles y soient complétées avant tout
 * encaissement réel.
 */
export function APropos() {
  useEffect(() => {
    document.title = "Qui édite STRATERA | STRATERA";
  }, []);

  return (
    <section id="contenu">
      <div className="shell narrow app">
        <div className="kicker">L'éditeur</div>
        <h1 style={{ marginTop: "6px" }}>Qui édite STRATERA</h1>
        <p className="sub">
          STRATERA est le diagnostic d'exposition professionnelle à l'IA générative — ce que vous
          utilisez. Il est édité par <b>RISEAFRICA</b>, qui développe des outils numériques pour les
          actifs d'Afrique francophone, aux côtés de Propulse. La marque désigne le produit ;
          l'éditeur est l'entité qui en répond.
        </p>

        <div className="block">
          <h2>Ce que nous faisons</h2>
          <p>
            Dire à un actif — salarié, indépendant, ou dirigeant de PME — quelles de ses tâches une
            IA générative réalise déjà, ce que ce temps représente, et par quoi commencer. Le
            diagnostic est gratuit ; un seul produit est payant, le rapport. La méthode est{" "}
            <a href="/methode">publiée intégralement</a>, les valeurs de référence sont dérivées de
            l'indice d'exposition de l'Organisation internationale du Travail (ILO Working
            Paper 140, 2025, CC BY 4.0 — une adaptation, non approuvée par l'OIT), et les chiffres
            que nous publions sont <a href="/barometre">citables au baromètre</a>.
          </p>
        </div>

        <div className="block">
          <h2>Ce que nous refusons de faire</h2>
          <ul className="listy">
            <li>
              <span>
                <b>Aucune donnée nominative n'est requise</b> pour obtenir un résultat. Pas de nom,
                pas de numéro, pas d'inscription.
              </span>
            </li>
            <li>
              <span>
                <b>Aucun texte de CV n'est conservé</b> : il est analysé puis abandonné. Et le
                salaire saisi par un dirigeant ne quitte jamais son appareil.
              </span>
            </li>
            <li>
              <span>
                <b>Aucun relevé n'est transmis à un employeur</b> — c'est ce qui permet de répondre
                honnêtement, et l'attestation ne se partage que si son titulaire le décide (
                <a href="/attestation">chacune est vérifiable ici</a>).
              </span>
            </li>
            <li>
              <span>
                <b>Pas de faux signaux</b> : pas de faux témoignages, pas de compteurs inventés, pas
                de comptes à rebours factices. Quand un chiffre est une estimation, l'écran le dit.
              </span>
            </li>
          </ul>
        </div>

        <div className="block">
          <h2>Nous écrire, ou aller plus loin</h2>
          <p className="fine">
            Les informations légales de l'éditeur, la politique de confidentialité et les conditions
            de vente — garantie de remboursement comprise — sont réunies dans les{" "}
            <a href="/#legal">mentions légales</a>. La méthode complète, ses limites et le journal
            des versions du barème sont sur la <a href="/methode">page méthode</a>.
          </p>
        </div>
      </div>
    </section>
  );
}
